import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover, Slider } from '@mui/material';
import TtsEmphasisIcon from '@src/assets/icons/tts-emphasis.svg';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import CommonButton from '@src/new-components/Button';
import { useNewClickOutside } from '@src/hooks/useNewClickOutside';
import Tooltip from '@src/components/Tooltip';
import Typography from '@src/new-components/Typography';
import { COLOR, GRADIENT_COLOR } from '@src/styles/color';
import {
  StyledEmphasisController,
  StyledEmphasisPopover,
  StyledEmphasisSlider,
  StyledPopoverContent,
  StyledPopoverHeader,
  StyledEmphasisAction,
} from './index.style';

const EmphasisPopover = ({
  currentEmphasis = 0,
  onChangeEmphasis,
  isDisabled = false,
}) => {
  const [emphasis, setEmphasis] = useState(currentEmphasis);
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);

  const [showWarningMessage, setShowWarningMessage] = useState(false);

  const popperContainerRef = useNewClickOutside(
    () => {
      setOpen(false);
      setEmphasis(currentEmphasis);
      setShowWarningMessage(currentEmphasis > 50);
    },
    ['mousedown'],
    null,
  );

  const handleClick = () => {
    setOpen((prev) => !prev);
  };

  const setLocalEmphasis = (value) => {
    setEmphasis(value);
    setShowWarningMessage(value > 50);
  };

  const handleApplyEmphasis = () => {
    onChangeEmphasis('emphasis', emphasis);
    setOpen(false);
  };

  const handleResetEmphasis = () => {
    setEmphasis(currentEmphasis);
    setShowWarningMessage(currentEmphasis > 50);
    setOpen(false);
  };

  const ValueLabelComponent = (props) => {
    const { children, value } = props;
    return (
      <Tooltip arrow title={value} placement="top">
        {children}
      </Tooltip>
    );
  };

  const renderArrowIcon = () => {
    if (open) {
      return (
        <ArrowDropUpRoundedIcon sx={{ fontSize: 24, color: COLOR.navyBlue }} />
      );
    }
    return (
      <ArrowDropDownRoundedIcon sx={{ fontSize: 24, color: COLOR.navyBlue }} />
    );
  };

  useEffect(() => {
    setEmphasis(currentEmphasis || 0);
    setShowWarningMessage(currentEmphasis && currentEmphasis > 50);
  }, [currentEmphasis]);

  return (
    <>
      <Tooltip arrow title={t('controlEmphasisLevel')} placement="top">
        <StyledEmphasisPopover
          onClick={isDisabled ? undefined : handleClick}
          role="button"
          tabIndex={0}
          ref={triggerRef}
          className={isDisabled ? 'disabled' : ''}
          currentEmphasis={currentEmphasis}
        >
          <div className="emphasis-icon-container">
            <img src={TtsEmphasisIcon} alt="tts-emphasis" />
          </div>
          <Typography variant="body-14-medium" color={COLOR.navyBlue}>
            {currentEmphasis !== null
              ? `${currentEmphasis}%`
              : t('chooseEmphasis')}
          </Typography>
          {renderArrowIcon()}
        </StyledEmphasisPopover>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={triggerRef.current}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          style: {
            zIndex: 999999,
            borderRadius: '8px',
            width: '400px',
            marginTop: '8px',
          },
          sx: {
            boxShadow: '0px 4px 16px 0px #00000014 !important',
          },
        }}
      >
        <div ref={popperContainerRef}>
          <StyledPopoverHeader>
            <Typography
              variant="body-16-bold"
              style={{ color: COLOR.black, padding: '4px 8px' }}
            >
              {t('emphasisPopoverTitle')}
            </Typography>
          </StyledPopoverHeader>
          <StyledPopoverContent>
            <Typography
              variant="body-14-regular"
              style={{ color: COLOR.black }}
            >
              {t('emphasisPopoverDescription')}
            </Typography>
            <StyledEmphasisController>
              <Typography
                variant="body-12-regular"
                style={{ color: COLOR.label2['64'] }}
              >
                {t('emphasisNote')}
              </Typography>
              <StyledEmphasisSlider>
                <Slider
                  value={emphasis}
                  onChange={(_, v) => {
                    setLocalEmphasis(v);
                  }}
                  step={10}
                  sx={{
                    height: 8,
                    padding: 0,
                    '& .MuiSlider-track': {
                      backgroundColor: `${COLOR.bluev2['80']}`,
                      height: 8,
                      color: `${COLOR.bluev2['80']}`,
                      border: 'none',
                    },
                    '& .MuiSlider-rail': {
                      backgroundColor: `${COLOR.bluev2['24']}`,
                      height: 8,
                    },
                    '& .MuiSlider-thumb': {
                      color: '#fff',
                      background: '#fff',
                      zIndex: 200,
                      boxShadow: '0px 3px 8px 0px #0000003D',
                    },
                    '& .MuiSlider-thumb:before': {
                      width: 16,
                      height: 16,
                      zIndex: 201,
                      background: `${GRADIENT_COLOR.blueGradient}`,
                      boxShadow: `0px 2px 8px 0px ${COLOR.bluev2['48']}`,
                    },
                    '& .MuiSlider-mark': {
                      backgroundColor: 'transparent',
                    },
                    '& .MuiSlider-thumb.Mui-focusVisible': {
                      boxShadow: 'none',
                    },
                  }}
                  valueLabelDisplay="auto"
                  components={{
                    ValueLabel: ValueLabelComponent,
                  }}
                />
                <Typography
                  variant="body-14-regular"
                  style={{ color: COLOR.label2['64'] }}
                >
                  {emphasis ? `${emphasis}%` : `${0}%`}
                </Typography>
              </StyledEmphasisSlider>
              {showWarningMessage && (
                <Typography
                  variant="body-12-regular"
                  style={{ color: COLOR.orangeV2['100'] }}
                >
                  {t('emphasisWarning')}
                </Typography>
              )}
            </StyledEmphasisController>
          </StyledPopoverContent>
          <StyledEmphasisAction>
            <CommonButton
              variant="outlined"
              textProps={{
                variantText: 'body-14-semibold',
                color: COLOR.darkBlue,
              }}
              customStyle={{
                padding: '8px 24px',
                height: '40px',
              }}
              onClick={handleResetEmphasis}
            >
              {t('cancel')}
            </CommonButton>
            <CommonButton
              variant="contained"
              textProps={{
                variantText: 'body-14-semibold',
                color: COLOR.darkBlue,
              }}
              customStyle={{
                padding: '8px 24px',
                height: '40px',
                flex: 1,
              }}
              onClick={handleApplyEmphasis}
            >
              {t('apply')}
            </CommonButton>
          </StyledEmphasisAction>
        </div>
      </Popover>
    </>
  );
};

export default EmphasisPopover;
